export class AudioManager {
    constructor() {
        this.context = null;
        this.sounds = {};
        this.voiceBuffers = {}; // Store loaded voice audio buffers
        this.musicGain = null;
        this.sfxGain = null;
        this.voiceGain = null;
        this.initialized = false;
        
        // Volume settings
        this.musicVolume = 0.3;
        this.sfxVolume = 0.5;
        this.voiceVolume = 0.7; // Increased to ~5dB louder for NPCs/bosses
        
        this.musicMode = 'calm'; // 'calm' or 'boss'
        this.musicLoopTimeout = null;
    }
    
    async initialize() {
        if (this.initialized) return;
        
        try {
            this.context = new (window.AudioContext || window.webkitAudioContext)();
            
            // Create listener for 3D audio
            this.listener = this.context.listener;
            
            // Create gain nodes for volume control
            this.musicGain = this.context.createGain();
            this.musicGain.gain.value = this.musicVolume;
            this.musicGain.connect(this.context.destination);
            
            this.sfxGain = this.context.createGain();
            this.sfxGain.gain.value = this.sfxVolume;
            this.sfxGain.connect(this.context.destination);
            
            this.voiceGain = this.context.createGain();
            this.voiceGain.gain.value = this.voiceVolume;
            this.voiceGain.connect(this.context.destination);
            
            this.initialized = true;
            console.log('🎵 Audio system initialized with 3D spatial audio');
            
            // Load voice files asynchronously
            this.loadVoiceFiles();
        } catch (error) {
            console.error('Failed to initialize audio:', error);
        }
    }
    
    updateListenerPosition(position, forward, up) {
        if (!this.initialized || !this.listener) return;
        
        // Update listener position (player camera)
        if (this.listener.positionX) {
            this.listener.positionX.value = position.x;
            this.listener.positionY.value = position.y;
            this.listener.positionZ.value = position.z;
        } else {
            this.listener.setPosition(position.x, position.y, position.z);
        }
        
        // Update listener orientation
        if (forward && up) {
            if (this.listener.forwardX) {
                this.listener.forwardX.value = forward.x;
                this.listener.forwardY.value = forward.y;
                this.listener.forwardZ.value = forward.z;
                this.listener.upX.value = up.x;
                this.listener.upY.value = up.y;
                this.listener.upZ.value = up.z;
            } else {
                this.listener.setOrientation(forward.x, forward.y, forward.z, up.x, up.y, up.z);
            }
        }
    }
    
    async loadVoiceFiles() {
        const voices = {
            clarence: ['clarence_1.mp3', 'clarence_2.mp3', 'clarence_3.mp3'],
            mike: ['mike_1.mp3', 'mike_2.mp3', 'mike_3.mp3'],
            lez: ['lez_1.mp3', 'lez_2.mp3', 'lez_3.mp3', 'lez_4.mp3'],
            sasquatch: ['sasquatch_1.mp3', 'sasquatch_2.mp3', 'sasquatch_3.mp3'],
            chooma: ['chooma_1.mp3', 'chooma_2.mp3', 'chooma_3.mp3', 'chooma_4.mp3']
        };
        
        for (const [character, files] of Object.entries(voices)) {
            this.voiceBuffers[character] = [];
            
            for (const file of files) {
                try {
                    const response = await fetch(`/sounds/${file}`);
                    const arrayBuffer = await response.arrayBuffer();
                    const audioBuffer = await this.context.decodeAudioData(arrayBuffer);
                    this.voiceBuffers[character].push(audioBuffer);
                } catch (error) {
                    console.warn(`Failed to load voice: ${file}`, error);
                }
            }
        }
        
        console.log('🎤 Voice files loaded:', Object.keys(this.voiceBuffers));
    }
    
    playVoice(character, position = null) {
        if (!this.initialized || !this.voiceBuffers[character] || this.voiceBuffers[character].length === 0) {
            return;
        }
        
        // Pick random voice line for this character
        const buffers = this.voiceBuffers[character];
        const buffer = buffers[Math.floor(Math.random() * buffers.length)];
        
        const source = this.context.createBufferSource();
        source.buffer = buffer;
        
        // Create 3D panner if position provided
        let panner = null;
        if (position) {
            panner = this.context.createPanner();
            panner.panningModel = 'HRTF';
            panner.distanceModel = 'inverse';
            panner.refDistance = 5;
            panner.maxDistance = 100;
            panner.rolloffFactor = 1;
            panner.coneInnerAngle = 360;
            panner.coneOuterAngle = 360;
            panner.coneOuterGain = 0;
            
            // Set position
            if (panner.positionX) {
                panner.positionX.value = position.x;
                panner.positionY.value = position.y;
                panner.positionZ.value = position.z;
            } else {
                panner.setPosition(position.x, position.y, position.z);
            }
        }
        
        // SPECIAL CHOOMA AUDIO PROCESSING!
        if (character === 'chooma') {
            // 1. PITCH: 3 semitones deeper
            const semitones = -3;
            source.playbackRate.value = Math.pow(2, semitones / 12);
            
            // 2. FM MODULATION (~3Hz vibrato)
            const lfo = this.context.createOscillator();
            const lfoGain = this.context.createGain();
            
            lfo.frequency.value = 3; // 3Hz modulation
            lfoGain.gain.value = 0.005; // Subtle pitch modulation
            
            lfo.connect(lfoGain);
            lfoGain.connect(source.playbackRate);
            lfo.start(this.context.currentTime);
            lfo.stop(this.context.currentTime + buffer.duration / source.playbackRate.value);
            
            // 3. Add 3rd HARMONIC (distortion/richness)
            const waveshaper = this.context.createWaveShaper();
            const curve = new Float32Array(256);
            for (let i = 0; i < 256; i++) {
                const x = (i - 128) / 128;
                // Add 3rd harmonic via waveshaping
                curve[i] = x + 0.15 * Math.sin(3 * Math.PI * x);
            }
            waveshaper.curve = curve;
            
            // 4. RANDOM PITCH VARIATION (exponential or parabolic)
            const useExponential = Math.random() < 0.5;
            const duration = buffer.duration / source.playbackRate.value;
            
            if (useExponential) {
                // Exponential curve: fast change then settle
                const startRate = source.playbackRate.value;
                const endRate = startRate * (0.95 + Math.random() * 0.1); // ±5%
                source.playbackRate.setValueAtTime(startRate, this.context.currentTime);
                source.playbackRate.exponentialRampToValueAtTime(endRate, this.context.currentTime + duration);
            } else {
                // Parabolic curve: rise then fall (or vice versa)
                const startRate = source.playbackRate.value;
                const midRate = startRate * (1 + (Math.random() - 0.5) * 0.15); // ±7.5%
                const endRate = startRate * (0.98 + Math.random() * 0.04); // slight variation
                
                source.playbackRate.setValueAtTime(startRate, this.context.currentTime);
                source.playbackRate.linearRampToValueAtTime(midRate, this.context.currentTime + duration / 2);
                source.playbackRate.linearRampToValueAtTime(endRate, this.context.currentTime + duration);
            }
            
            // Connect through processing chain with positional audio
            source.connect(waveshaper);
            if (panner) {
                waveshaper.connect(panner);
                panner.connect(this.voiceGain);
                console.log(`🍄 Chooma voice (3D positional) with FM modulation, 3rd harmonic, ${useExponential ? 'exponential' : 'parabolic'} pitch!`);
            } else {
                waveshaper.connect(this.voiceGain);
                console.log(`🍄 Chooma voice with FM modulation, 3rd harmonic, ${useExponential ? 'exponential' : 'parabolic'} pitch!`);
            }
        } else {
            // Normal voice playback for other characters with positional audio
            if (panner) {
                source.connect(panner);
                panner.connect(this.voiceGain);
                console.log(`🎤 ${character} says something! (3D positional)`);
            } else {
                source.connect(this.voiceGain);
                console.log(`🎤 ${character} says something!`);
            }
        }
        
        source.start(0);
    }
    
    // Generate procedural sound effects
    playShoot() {
        if (!this.initialized) return;
        
        const oscillator = this.context.createOscillator();
        const gainNode = this.context.createGain();
        
        oscillator.connect(gainNode);
        gainNode.connect(this.sfxGain);
        
        oscillator.type = 'square';
        oscillator.frequency.setValueAtTime(200, this.context.currentTime);
        oscillator.frequency.exponentialRampToValueAtTime(50, this.context.currentTime + 0.1);
        
        gainNode.gain.setValueAtTime(0.3, this.context.currentTime);
        gainNode.gain.exponentialRampToValueAtTime(0.01, this.context.currentTime + 0.1);
        
        oscillator.start(this.context.currentTime);
        oscillator.stop(this.context.currentTime + 0.1);
    }
    
    playHit() {
        if (!this.initialized) return;
        
        const oscillator = this.context.createOscillator();
        const gainNode = this.context.createGain();
        
        oscillator.connect(gainNode);
        gainNode.connect(this.sfxGain);
        
        oscillator.type = 'sawtooth';
        oscillator.frequency.setValueAtTime(150, this.context.currentTime);
        oscillator.frequency.exponentialRampToValueAtTime(30, this.context.currentTime + 0.15);
        
        gainNode.gain.setValueAtTime(0.15, this.context.currentTime); // Reduced from 0.4 - quieter hit sounds
        gainNode.gain.exponentialRampToValueAtTime(0.01, this.context.currentTime + 0.15);
        
        oscillator.start(this.context.currentTime);
        oscillator.stop(this.context.currentTime + 0.15);
    }
    
    playEssenceCollect() {
        if (!this.initialized) return;
        
        const oscillator = this.context.createOscillator();
        const gainNode = this.context.createGain();
        
        oscillator.connect(gainNode);
        gainNode.connect(this.sfxGain);
        
        oscillator.type = 'sine';
        oscillator.frequency.setValueAtTime(800, this.context.currentTime);
        oscillator.frequency.exponentialRampToValueAtTime(1200, this.context.currentTime + 0.2);
        
        gainNode.gain.setValueAtTime(0.2, this.context.currentTime);
        gainNode.gain.exponentialRampToValueAtTime(0.01, this.context.currentTime + 0.2);
        
        oscillator.start(this.context.currentTime);
        oscillator.stop(this.context.currentTime + 0.2);
    }
    
    playMushroomPickup() {
        if (!this.initialized) return;
        
        // Happy mushroom pickup sound - bouncy!
        const times = [0, 0.1, 0.2];
        const frequencies = [600, 800, 1000];
        
        times.forEach((time, i) => {
            const oscillator = this.context.createOscillator();
            const gainNode = this.context.createGain();
            
            oscillator.connect(gainNode);
            gainNode.connect(this.sfxGain);
            
            oscillator.type = 'triangle';
            oscillator.frequency.setValueAtTime(frequencies[i], this.context.currentTime + time);
            
            gainNode.gain.setValueAtTime(0.3, this.context.currentTime + time);
            gainNode.gain.exponentialRampToValueAtTime(0.01, this.context.currentTime + time + 0.15);
            
            oscillator.start(this.context.currentTime + time);
            oscillator.stop(this.context.currentTime + time + 0.15);
        });
    }
    
    playEvolutionReady() {
        if (!this.initialized) return;
        
        // Bright, fast ascending arpeggio - 2x faster, major key!
        // C major chord ascent: C5, E5, G5, C6
        const times = [0, 0.05, 0.1, 0.15];
        const frequencies = [523.25, 659.25, 783.99, 1046.50]; // C5, E5, G5, C6
        
        times.forEach((time, i) => {
            const oscillator = this.context.createOscillator();
            const gainNode = this.context.createGain();
            
            oscillator.connect(gainNode);
            gainNode.connect(this.sfxGain);
            
            oscillator.type = 'triangle';
            oscillator.frequency.setValueAtTime(frequencies[i], this.context.currentTime + time);
            
            gainNode.gain.setValueAtTime(0.35, this.context.currentTime + time);
            gainNode.gain.exponentialRampToValueAtTime(0.01, this.context.currentTime + time + 0.075);
            
            oscillator.start(this.context.currentTime + time);
            oscillator.stop(this.context.currentTime + time + 0.075);
        });
    }
    
    playEvolution() {
        if (!this.initialized) return;
        
        // Ascending arpeggio for evolution
        const notes = [440, 554.37, 659.25, 880];
        
        notes.forEach((freq, i) => {
            setTimeout(() => {
                const oscillator = this.context.createOscillator();
                const gainNode = this.context.createGain();
                
                oscillator.connect(gainNode);
                gainNode.connect(this.sfxGain);
                
                oscillator.type = 'sine';
                oscillator.frequency.setValueAtTime(freq, this.context.currentTime);
                
                gainNode.gain.setValueAtTime(0.3, this.context.currentTime);
                gainNode.gain.exponentialRampToValueAtTime(0.01, this.context.currentTime + 0.3);
                
                oscillator.start(this.context.currentTime);
                oscillator.stop(this.context.currentTime + 0.3);
            }, i * 100);
        });
    }
    
    playDamage() {
        if (!this.initialized) return;
        
        const oscillator = this.context.createOscillator();
        const gainNode = this.context.createGain();
        
        oscillator.connect(gainNode);
        gainNode.connect(this.sfxGain);
        
        oscillator.type = 'sawtooth';
        oscillator.frequency.setValueAtTime(100, this.context.currentTime);
        
        gainNode.gain.setValueAtTime(0.5, this.context.currentTime);
        gainNode.gain.exponentialRampToValueAtTime(0.01, this.context.currentTime + 0.1);
        
        oscillator.start(this.context.currentTime);
        oscillator.stop(this.context.currentTime + 0.1);
    }
    
    playJump() {
        if (!this.initialized) return;
        
        const oscillator = this.context.createOscillator();
        const gainNode = this.context.createGain();
        
        oscillator.connect(gainNode);
        gainNode.connect(this.sfxGain);
        
        oscillator.type = 'sine';
        oscillator.frequency.setValueAtTime(300, this.context.currentTime);
        oscillator.frequency.exponentialRampToValueAtTime(500, this.context.currentTime + 0.15);
        
        gainNode.gain.setValueAtTime(0.2, this.context.currentTime);
        gainNode.gain.exponentialRampToValueAtTime(0.01, this.context.currentTime + 0.15);
        
        oscillator.start(this.context.currentTime);
        oscillator.stop(this.context.currentTime + 0.15);
    }
    
    // Ambient background music loop
    startBackgroundMusic() {
        if (!this.initialized) return;
        
        this.musicMode = 'calm';
        this.playBackgroundLoop();
    }
    
    switchToBossMusic() {
        if (!this.initialized) return;
        
        this.musicMode = 'boss';
        // Clear current loop
        if (this.musicLoopTimeout) {
            clearTimeout(this.musicLoopTimeout);
        }
        this.playBossLoop();
    }
    
    switchToCalmMusic() {
        if (!this.initialized) return;
        
        this.musicMode = 'calm';
        // Clear current loop
        if (this.musicLoopTimeout) {
            clearTimeout(this.musicLoopTimeout);
        }
        this.playBackgroundLoop();
    }
    
    playBackgroundLoop() {
        if (!this.initialized || this.musicMode !== 'calm') return;
        
        // Psychedelic ambient pad
        const notes = [220, 246.94, 277.18, 293.66]; // A, B, C#, D
        const duration = 4; // 4 seconds per note
        
        notes.forEach((freq, i) => {
            setTimeout(() => {
                if (this.musicMode !== 'calm') return;
                
                const oscillator = this.context.createOscillator();
                const gainNode = this.context.createGain();
                const filter = this.context.createBiquadFilter();
                
                oscillator.connect(filter);
                filter.connect(gainNode);
                gainNode.connect(this.musicGain);
                
                oscillator.type = 'sine';
                oscillator.frequency.setValueAtTime(freq, this.context.currentTime);
                
                filter.type = 'lowpass';
                filter.frequency.setValueAtTime(800, this.context.currentTime);
                
                gainNode.gain.setValueAtTime(0, this.context.currentTime);
                gainNode.gain.linearRampToValueAtTime(0.15, this.context.currentTime + 0.5);
                gainNode.gain.setValueAtTime(0.15, this.context.currentTime + duration - 0.5);
                gainNode.gain.linearRampToValueAtTime(0, this.context.currentTime + duration);
                
                oscillator.start(this.context.currentTime);
                oscillator.stop(this.context.currentTime + duration);
            }, i * duration * 1000);
        });
        
        // Loop the music
        this.musicLoopTimeout = setTimeout(() => {
            this.playBackgroundLoop();
        }, notes.length * duration * 1000);
    }
    
    playBossLoop() {
        if (!this.initialized || this.musicMode !== 'boss') return;
        
        // EVIL boss music - IMPROVED MELODY
        // Using Phrygian mode (darkest mode), dramatic leaps, diminished intervals
        // Key: D minor/Phrygian - the most evil-sounding mode
        
        // Main bass riff - dramatic tension/release pattern
        // D2 → Eb2 (half step, tense!) → A1 (leap down, ominous) → C2 → D2 (return) → G2 (leap up!) → Bb2 (diminished) → A2 (resolve to tension)
        const bassNotes = [
            73.42,  // D2 - root, establish key
            77.78,  // Eb2 - half step up (Phrygian! very dark)
            55.00,  // A1 - dramatic leap down (perfect 4th below root, ominous)
            65.41,  // C2 - minor third, building
            73.42,  // D2 - return to root
            98.00,  // G2 - leap up to 4th (tension!)
            116.54, // Bb2 - diminished 5th interval (devil's interval!)
            110.00  // A2 - dominant, unstable ending
        ];
        
        // High dissonant stabs - tritone and diminished chord hits
        // Using interval jumps that clash with bass
        const tritoneNotes = [
            146.83, // D3 - octave of root
            155.56, // Eb3 - evil half step
            207.65, // Ab3 - tritone! (devil's interval from D)
            233.08, // Bb3 - more tritone
            220.00, // A3 - tension
            185.00, // F#3 - another tritone
            207.65, // Ab3 - repeat evil note
            196.00  // G3 - suspended tension
        ];
        
        const duration = 0.9; // Faster, more urgent tempo
        
        // Main bass line (low, ominous)
        bassNotes.forEach((freq, i) => {
            setTimeout(() => {
                if (this.musicMode !== 'boss') return;
                
                // Bass oscillator (thick, distorted)
                const osc1 = this.context.createOscillator();
                const osc2 = this.context.createOscillator();
                const gainNode = this.context.createGain();
                const distortion = this.context.createWaveShaper();
                const filter = this.context.createBiquadFilter();
                
                // Create distortion curve for evil sound
                const curve = new Float32Array(256);
                for (let j = 0; j < 256; j++) {
                    const x = (j - 128) / 128;
                    curve[j] = Math.tanh(x * 3); // Soft clipping
                }
                distortion.curve = curve;
                
                osc1.connect(distortion);
                osc2.connect(distortion);
                distortion.connect(filter);
                filter.connect(gainNode);
                gainNode.connect(this.musicGain);
                
                osc1.type = 'sawtooth';
                osc2.type = 'square'; // Add square for extra harshness
                osc1.frequency.setValueAtTime(freq, this.context.currentTime);
                osc2.frequency.setValueAtTime(freq * 0.99, this.context.currentTime); // Slight detune for thickness
                
                filter.type = 'lowpass';
                filter.frequency.setValueAtTime(400, this.context.currentTime); // Very dark
                filter.Q.setValueAtTime(8, this.context.currentTime); // Resonant for evil quality
                
                // Pulsing envelope for tension
                gainNode.gain.setValueAtTime(0, this.context.currentTime);
                gainNode.gain.linearRampToValueAtTime(0.25, this.context.currentTime + 0.05);
                gainNode.gain.setValueAtTime(0.25, this.context.currentTime + 0.4);
                gainNode.gain.linearRampToValueAtTime(0.15, this.context.currentTime + 0.6);
                gainNode.gain.linearRampToValueAtTime(0, this.context.currentTime + duration);
                
                osc1.start(this.context.currentTime);
                osc1.stop(this.context.currentTime + duration);
                osc2.start(this.context.currentTime);
                osc2.stop(this.context.currentTime + duration);
            }, i * duration * 1000);
        });
        
        // High dissonant layer (tritone intervals - the "devil's interval")
        tritoneNotes.forEach((freq, i) => {
            setTimeout(() => {
                if (this.musicMode !== 'boss') return;
                
                const oscillator = this.context.createOscillator();
                const gainNode = this.context.createGain();
                const filter = this.context.createBiquadFilter();
                
                oscillator.connect(filter);
                filter.connect(gainNode);
                gainNode.connect(this.musicGain);
                
                oscillator.type = 'triangle'; // Hollow, eerie sound
                oscillator.frequency.setValueAtTime(freq, this.context.currentTime);
                
                filter.type = 'bandpass';
                filter.frequency.setValueAtTime(800, this.context.currentTime);
                filter.Q.setValueAtTime(5, this.context.currentTime);
                
                // Stab effect - match bass rhythm
                gainNode.gain.setValueAtTime(0.15, this.context.currentTime);
                gainNode.gain.exponentialRampToValueAtTime(0.01, this.context.currentTime + 0.6);
                
                oscillator.start(this.context.currentTime);
                oscillator.stop(this.context.currentTime + 0.6);
            }, i * duration * 1000); // Match bass timing for tighter feel
        });
        
        // Sub bass pulse (felt in chest, very evil)
        setTimeout(() => {
            if (this.musicMode !== 'boss') return;
            
            const subOsc = this.context.createOscillator();
            const subGain = this.context.createGain();
            
            subOsc.connect(subGain);
            subGain.connect(this.musicGain);
            
            subOsc.type = 'sine';
            subOsc.frequency.setValueAtTime(36.71, this.context.currentTime); // D1 - very low, matches new key
            
            // Pulsing sub bass
            subGain.gain.setValueAtTime(0, this.context.currentTime);
            subGain.gain.linearRampToValueAtTime(0.3, this.context.currentTime + 0.1);
            subGain.gain.linearRampToValueAtTime(0, this.context.currentTime + 0.5);
            subGain.gain.linearRampToValueAtTime(0.3, this.context.currentTime + 0.7);
            subGain.gain.linearRampToValueAtTime(0, this.context.currentTime + 1.2);
            
            subOsc.start(this.context.currentTime);
            subOsc.stop(this.context.currentTime + bassNotes.length * duration);
        }, 0);
        
        // Loop the boss music
        this.musicLoopTimeout = setTimeout(() => {
            this.playBossLoop();
        }, bassNotes.length * duration * 1000);
    }
    
    setMusicVolume(volume) {
        this.musicVolume = Math.max(0, Math.min(1, volume));
        if (this.musicGain) {
            this.musicGain.gain.value = this.musicVolume;
        }
    }
    
    setSFXVolume(volume) {
        this.sfxVolume = Math.max(0, Math.min(1, volume));
        if (this.sfxGain) {
            this.sfxGain.gain.value = this.sfxVolume;
        }
    }
    
    playDeathMusic() {
        if (!this.initialized) return;
        
        // Sad, descending death music
        const now = this.context.currentTime;
        const osc = this.context.createOscillator();
        const gain = this.context.createGain();
        
        osc.type = 'sine';
        osc.connect(gain);
        gain.connect(this.musicGain);
        
        // Descending notes: C4 -> Bb3 -> Ab3 -> F3 (sad progression)
        const notes = [261.63, 233.08, 207.65, 174.61];
        const duration = 0.8;
        
        notes.forEach((freq, i) => {
            const time = now + i * duration;
            osc.frequency.setValueAtTime(freq, time);
            gain.gain.setValueAtTime(0.3, time);
            gain.gain.exponentialRampToValueAtTime(0.001, time + duration * 0.9);
        });
        
        osc.start(now);
        osc.stop(now + notes.length * duration);
        
        console.log('💀 Death music playing...');
    }
    
    playStartMusic() {
        if (!this.initialized) return;
        
        // Uplifting start music with melody
        const now = this.context.currentTime;
        const melody = [
            { freq: 261.63, dur: 0.3 }, // C4
            { freq: 329.63, dur: 0.3 }, // E4
            { freq: 392.00, dur: 0.3 }, // G4
            { freq: 523.25, dur: 0.6 }  // C5 (hold)
        ];
        
        melody.forEach((note, i) => {
            const osc = this.context.createOscillator();
            const gain = this.context.createGain();
            
            osc.type = 'triangle';
            osc.frequency.value = note.freq;
            osc.connect(gain);
            gain.connect(this.musicGain);
            
            const startTime = now + melody.slice(0, i).reduce((sum, n) => sum + n.dur, 0);
            gain.gain.setValueAtTime(0.25, startTime);
            gain.gain.exponentialRampToValueAtTime(0.001, startTime + note.dur);
            
            osc.start(startTime);
            osc.stop(startTime + note.dur);
        });
        
        console.log('🎵 Start music playing!');
    }
    
    playVictoryMusic() {
        if (!this.initialized) return;
        
        // Triumphant victory fanfare
        const now = this.context.currentTime;
        const fanfare = [
            { freq: 523.25, dur: 0.2 }, // C5
            { freq: 523.25, dur: 0.2 }, // C5
            { freq: 587.33, dur: 0.2 }, // D5
            { freq: 659.25, dur: 0.2 }, // E5
            { freq: 783.99, dur: 0.6 }  // G5 (hold)
        ];
        
        fanfare.forEach((note, i) => {
            const osc = this.context.createOscillator();
            const gain = this.context.createGain();
            
            osc.type = 'square';
            osc.frequency.value = note.freq;
            osc.connect(gain);
            gain.connect(this.musicGain);
            
            const startTime = now + fanfare.slice(0, i).reduce((sum, n) => sum + n.dur, 0);
            gain.gain.setValueAtTime(0.3, startTime);
            gain.gain.exponentialRampToValueAtTime(0.001, startTime + note.dur);
            
            osc.start(startTime);
            osc.stop(startTime + note.dur);
        });
        
        console.log('🏆 Victory music playing!');
    }
    
    pause() {
        // Suspend audio context to pause all sounds
        if (this.initialized && this.context && this.context.state === 'running') {
            this.context.suspend();
            console.log('⏸️ Audio paused');
        }
    }
    
    resume() {
        // Resume audio context
        if (this.initialized && this.context && this.context.state === 'suspended') {
            this.context.resume();
            console.log('▶️ Audio resumed');
        }
    }
}
